import { Trans, useLingui } from '@lingui/react/macro';

import { PriceAmountWithDetails } from '@/api';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconInfo from '@/icons/Info.svg';
import { Currency, formatCurrency } from '@/utils/number';
import useViewPort from '@/utils/useViewport';

export const ClientRequestPrice = ({
  price,
}: {
  price: PriceAmountWithDetails | undefined | null;
}) => {
  const { isMobile } = useViewPort();
  const { t } = useLingui();

  const hasRefashionDiscount = !!price?.details.find(
    (detail) => detail.type === 'discount' && detail.subType === 'refashion' && detail.amount !== 0
  );
  const hasRateDiscounts = !!price?.details.find(
    (detail) => detail.type === 'discount' && detail.subType !== 'refashion' && detail.amount !== 0
  );

  const clientShippingManagementFee = price?.details
    .filter((priceDetail) => priceDetail.type === 'fee')
    .find((fee) => fee.subType === 'client-shipping-management');

  const clientHandlingFee = price?.details
    .filter((priceDetail) => priceDetail.type === 'fee')
    .find((fee) => fee.subType === 'client-handling');

  const paidEstimateAdvanceFee = price?.details
    .filter((priceDetail) => priceDetail.type === 'fee')
    .find((fee) => fee.subType === 'paid-estimate-advance');

  const priceWithoutFeeAmount =
    (price?.amount ?? 0) -
    (clientShippingManagementFee?.amount ?? 0) -
    (clientHandlingFee?.amount ?? 0) -
    (paidEstimateAdvanceFee?.amount ?? 0);

  return (
    <Stack
      className="bg-neutral-300"
      gap="0.5rem"
      padding="0.75rem 1rem"
      style={{ borderRadius: '0.5rem' }}
    >
      <Stack>
        <Stack gap="2px">
          <Stack row flexWrap="nowrap" justifyContent="space-between">
            <Paragraph size="200" color="secondary">
              {isMobile ? (
                <Trans id="client.articles.table.footer.subtotal">Subtotal</Trans>
              ) : hasRateDiscounts && hasRefashionDiscount ? (
                <Trans id="client.articles.table.footer.subtotal-bonus-and-discount">
                  Subtotal with discount and repair bonus
                </Trans>
              ) : hasRateDiscounts ? (
                <Trans id="client.articles.table.footer.subtotal-discount">
                  Subtotal with discount
                </Trans>
              ) : hasRefashionDiscount ? (
                <Trans id="client.articles.table.footer.subtotal-bonus">
                  Subtotal with repair bonus
                </Trans>
              ) : (
                <Trans id="client.articles.table.footer.subtotal">Subtotal</Trans>
              )}
            </Paragraph>
            <Paragraph size="200" color="secondary" whiteSpace="nowrap">
              {!!price && (hasRefashionDiscount || hasRateDiscounts) && (
                <>
                  <DiscountDetail details={price.details} />{' '}
                </>
              )}
              {formatCurrency(priceWithoutFeeAmount, price?.currency)}
            </Paragraph>
          </Stack>
          {!!clientShippingManagementFee && (
            <Stack row flexWrap="nowrap" justifyContent="space-between">
              <Paragraph size="200" color="secondary">
                <Trans id="client.articles.table.total.shipment.label">Shipping fees</Trans>
              </Paragraph>
              <>
                {clientShippingManagementFee.amount ? (
                  <Paragraph size="200" color="secondary">
                    {formatCurrency(clientShippingManagementFee.amount, price?.currency)}{' '}
                  </Paragraph>
                ) : (
                  <Paragraph size="200" color="secondary">
                    <Trans id="client.articles.table.total.shipment.free">Free</Trans>
                  </Paragraph>
                )}
              </>
            </Stack>
          )}
          {!!clientHandlingFee && (
            <Stack row flexWrap="nowrap" justifyContent="space-between">
              <Paragraph size="200" color="secondary">
                <Trans id="price-with-details.rows.client-handling">Handling fee</Trans>
              </Paragraph>
              {clientHandlingFee.amount ? (
                <Paragraph size="200" color="secondary">
                  {formatCurrency(clientHandlingFee.amount, clientHandlingFee.currency)}{' '}
                </Paragraph>
              ) : (
                <Paragraph size="200" color="secondary">
                  <Trans id="client.articles.table.total.shipment.free">Free</Trans>
                </Paragraph>
              )}
            </Stack>
          )}
          {!!paidEstimateAdvanceFee?.amount && (
            <Stack row flexWrap="nowrap" justifyContent="space-between">
              <Paragraph size="200" color="secondary">
                <Trans id="price-with-details.rows.paid-estimate-advance">
                  Paid estimate advance
                </Trans>
              </Paragraph>
              <Paragraph size="200" color="secondary">
                {formatCurrency(
                  paidEstimateAdvanceFee.amount,
                  paidEstimateAdvanceFee.currency
                )}{' '}
              </Paragraph>
            </Stack>
          )}
        </Stack>
        <Stack
          row
          flexWrap="nowrap"
          justifyContent="space-between"
          ariaLabel={t({ id: 'client.articles.table.footer.total-price', message: 'Total price' })}
        >
          <Paragraph weight="medium">
            <Trans id="client.articles.table.footer.total-simple">Total</Trans>
          </Paragraph>
          <Paragraph weight="medium">{formatCurrency(price?.amount, price?.currency)}</Paragraph>
        </Stack>
      </Stack>
    </Stack>
  );
};

const DiscountDetail = ({ details }: { details: PriceAmountWithDetails['details'] }) => {
  const { t } = useLingui();

  const groupedDiscounts = details.reduce<
    {
      type: 'refashion' | 'rate';
      amount: number;
      currency: Currency;
    }[]
  >((acc, detail) => {
    if (detail.type === 'discount' && detail.amount !== 0) {
      const type = detail.subType === 'refashion' ? 'refashion' : 'rate';
      const existingDiscount = acc.find((discount) => discount.type === type);

      if (existingDiscount) {
        existingDiscount.amount += detail.amount;
      } else {
        acc.push({
          type,
          amount: detail.amount,
          currency: detail.currency,
        });
      }
    }

    return acc;
  }, []);

  const labelForType = {
    refashion: t({ id: 'article-money-cell.refashion', message: 'Bonus amount' }),
    rate: t({ id: 'article-money-cell.discount', message: 'Discount amount' }),
  };

  return (
    <Tooltip
      content={
        <Stack>
          {groupedDiscounts?.map((discount) => (
            <p key={discount.type}>
              {labelForType[discount.type]}: {formatCurrency(discount.amount, discount.currency)}
            </p>
          ))}
        </Stack>
      }
    >
      <Button variant="style-less" style={{ verticalAlign: 'text-top' }}>
        <IconInfo style={{ fontSize: '1rem' }} />
      </Button>
    </Tooltip>
  );
};
